/**
 * Estado de vigencia de una cotización.
 *
 * Es el dato más importante de la lista: una cotización vencida no se puede
 * prometer, porque el tarifario cambia cada semana. Por eso la vigencia va como
 * columna propia y no escondida en el detalle.
 *
 * Puro y sin dependencias, para poder testear los bordes (hoy mismo, mañana,
 * ayer) sin montar la pantalla.
 */

export type QuoteValidityState = 'valid' | 'expiring' | 'expired' | 'unknown';

export interface QuoteValidity {
  state: QuoteValidityState;
  /** Días que faltan. Negativo si ya venció. `null` si no hay fecha. */
  daysLeft: number | null;
  /** Texto listo para mostrar: "en 5 días", "vence hoy", "venció hace 2 días". */
  label: string;
}

/**
 * A partir de cuántos días restantes se considera "por vencer".
 *
 * Dos días: con el tarifario cambiando cada semana, avisar antes es ruido y
 * avisar después es tarde.
 */
export const EXPIRING_SOON_DAYS = 2;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Días entre dos fechas, contando por día calendario y no por horas.
 *
 * Si se contaran horas, una cotización que vence hoy a las 23:59 mostraría
 * "en 0 días" a la mañana y "vencida" a la tarde. Lo que importa es el día.
 */
function calendarDaysBetween(from: Date, to: Date): number {
  const a = Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate());
  const b = Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate());
  return Math.round((b - a) / MS_PER_DAY);
}

export function quoteValidity(
  validUntil: string | Date | null | undefined,
  now: Date = new Date()
): QuoteValidity {
  if (!validUntil) {
    return { state: 'unknown', daysLeft: null, label: 'sin vigencia' };
  }

  const until = validUntil instanceof Date ? validUntil : new Date(validUntil);
  if (Number.isNaN(until.getTime())) {
    return { state: 'unknown', daysLeft: null, label: 'sin vigencia' };
  }

  const daysLeft = calendarDaysBetween(now, until);

  if (daysLeft < 0) {
    const days = Math.abs(daysLeft);
    return {
      state: 'expired',
      daysLeft,
      label: days === 1 ? 'venció ayer' : `venció hace ${days} días`,
    };
  }

  if (daysLeft === 0) {
    return { state: 'expiring', daysLeft, label: 'vence hoy' };
  }

  const label = daysLeft === 1 ? 'vence mañana' : `en ${daysLeft} días`;
  return {
    state: daysLeft <= EXPIRING_SOON_DAYS ? 'expiring' : 'valid',
    daysLeft,
    label,
  };
}

/** Etiqueta corta del estado, para los filtros y los chips. */
export const VALIDITY_LABEL: Record<QuoteValidityState, string> = {
  valid: 'Vigente',
  expiring: 'Por vencer',
  expired: 'Vencida',
  unknown: 'Sin vigencia',
};
